import PropTypes from 'prop-types';
import React from 'react';
import { useFlags } from 'wsm-common-data';
import ColorSwatch from './ColorSwatch';
import '../global-sass/main.scss';
import {
	EV_QUICK_SPECS,
	FLAG_ENABLE_EV_DETAIL_LINK,
	FLAG_USE_QUICK_SPECS_FROM_IDC
} from '../global-constants';
import DetailsLink from './DetailsLink';

// https://stackoverflow.com/questions/2901102/how-to-print-a-number-with-commas-as-thousands-separators-in-javascript
const numberWithCommas = (x) => {
	return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
const getEvDetailsContent = (labels, contents, isGroupEV = false) => {
	const subText = labels.get('EV_QUICKSPECDETAILS_SPEC_SUB_TEXT_VERBIAGE');
	if (isGroupEV) {
		const evQuickSpecsDetails = contents
			.map(({ key, titleLabel, detailLabel, isPicked }) => {
				return {
					key,
					title: labels.get(titleLabel),
					details: labels.get(detailLabel),
					isPicked
				};
			})
			.filter((item) => item.details);
		return {
			mainDetail: null,
			subContent: evQuickSpecsDetails,
			subText
		};
	}

	return {
		details:
			labels.get(contents) !== contents ? labels.get(contents) : null,
		subText
	};
};

const openEvQuickSepcModal = (labels, onOpenModal, key, evDataContent) => {
	const evDetailsSubContent = evDataContent.subContent.map((evItem) => ({
		...evItem,
		isPicked: evItem.key === key
	}));
	const groupTitleLabel = labels.get('EV_QUICKSPECDETAILS_SPEC_TITLE');
	onOpenModal(
		groupTitleLabel,
		{ ...evDataContent, subContent: evDetailsSubContent },
		true
	);
};

const renderSpecs = ({
	quickSpecs,
	labels,
	vehicleType,
	isElectric,
	onOpenModal,
	showEvDetailsLinkFlag,
	useIDCAttrs
}) => {
	const {
		bodyStyle,
		cityFuelEconomy,
		driveLine,
		engine,
		engineSize,
		exteriorColor,
		normalExteriorColor,
		highwayFuelEconomy,
		interiorColor,
		normalInteriorColor,
		maxSeatingCapacity,
		odometer,
		transmission,
		combinedFuelEfficiency,
		range,
		batteryWarranty
	} = quickSpecs;

	// Not showing the odometer field when vehicle type is 'NEW'
	const odometerValue =
		odometer &&
		vehicleType !== 'NEW' &&
		`${numberWithCommas(odometer)} ${labels.get('MILES_KILOMETERS')}`;

	const fuelEconomyValue =
		cityFuelEconomy &&
		typeof cityFuelEconomy === 'number' &&
		highwayFuelEconomy &&
		typeof highwayFuelEconomy === 'number' &&
		// remove 0 behind decimal if exist
		`${parseFloat(cityFuelEconomy.toFixed(1))}/${parseFloat(
			highwayFuelEconomy.toFixed(1)
		)} ${labels.get('MPG_CITY_HWY')}`;

	const batteryWarrantyValue = vehicleType !== 'NEW' ? null : batteryWarranty;

	const transmissionValue = isElectric ? null : transmission;

	const extColorText = exteriorColor || normalExteriorColor;
	const intColorText = interiorColor || normalInteriorColor;

	const extColorSwatch = (
		<ColorSwatch
			normalizedColor={normalExteriorColor}
			fullColor={exteriorColor}
		/>
	);

	const intColorSwatch = (
		<ColorSwatch
			normalizedColor={normalInteriorColor}
			fullColor={interiorColor}
		/>
	);

	let bodySeatingKey;
	let bodySeatingValue;
	if (bodyStyle && maxSeatingCapacity) {
		bodySeatingKey = labels.get('BODY_SEATING');
		bodySeatingValue = `${bodyStyle}/${maxSeatingCapacity} ${labels.get(
			'SEATS'
		)}`;
	} else if (maxSeatingCapacity) {
		// This is a fallback in case bodyStyle is not available
		// but this should not be needed and can potentially be removed for simplicity
		bodySeatingKey = labels.get('SEATING');
		bodySeatingValue = `${maxSeatingCapacity} ${labels.get('SEATS')}`;
	}

	let engineValue;
	if (isElectric) {
		engineValue = null;
	} else if (engineSize && engine) {
		engineValue = `${engineSize} ${engine}`;
	} else if (engine) {
		engineValue = engine;
	} else if (engineSize) {
		engineValue = engineSize;
	}

	let specObj = {};

	// if use idc flag is true
	// create array of keys from quickSpecs obj
	// this should be order
	if (useIDCAttrs) {
		const labelsObj = {
			exteriorColor: labels.get('EXTERIOR_COLOR'),
			interiorColor: labels.get('INTERIOR_COLOR'),
			odometer: labels.get('ODOMETER'),
			maxSeatingCapacity: bodySeatingKey,
			year: labels.get('YEAR'),
			trim: labels.get('TRIM'),
			vin: labels.get('VIN'),
			stockNumber: labels.get('STOCK_NUMBER'),
			fuelEconomy: labels.get('FUEL_ECONOMY'),
			transmission: labels.get('TRANSMISSION'),
			modelCode: labels.get('MODEL_CODE'),
			engine: labels.get('ENGINE'),
			driveLine: labels.get('DRIVETRAIN'),
			model: labels.get('MODEL'),
			doors: labels.get('DOORS'),
			fuelType: labels.get('FUEL_TYPE'),
			cab: labels.get('CAB'),
			make: labels.get('MAKE'),
			electricRange: labels.get('EV_RANGE'),
			combinedFuelEfficiency: labels.get('COMBINED_FUEL_ECONOMY'),
			batteryWarranty: labels.get('EV_BATTERY_WARRANTY')
		};
		const specKeys = Object.keys(quickSpecs).filter(
			(attr) => !!labelsObj[attr]
		);
		specKeys.forEach((attr) => {
			if (
				!specObj[labelsObj[attr]] &&
				typeof quickSpecs[attr] !== 'object'
			) {
				if (attr === 'maxSeatingCapacity') {
					specObj[labelsObj[attr]] = bodySeatingValue;
				} else if (attr === 'engine') {
					specObj[labelsObj[attr]] = engineValue;
				} else if (attr === 'fuelEconomy') {
					specObj[labelsObj[attr]] = fuelEconomyValue;
				} else if (attr === 'odometer') {
					specObj[labelsObj[attr]] = odometerValue;
				} else {
					specObj[labelsObj[attr]] = quickSpecs[attr];
				}
			}
		});
	} else {
		// TODO: remove this block once FF is permanent
		specObj = {
			[labels.get('EXTERIOR_COLOR')]: extColorText,
			[labels.get('INTERIOR_COLOR')]: intColorText,
			[labels.get('ODOMETER')]: odometerValue,
			[labels.get('FUEL_ECONOMY')]: fuelEconomyValue,
			[bodySeatingKey]: bodySeatingValue,
			[labels.get('EV_RANGE')]: range,
			[labels.get('COMBINED_FUEL_ECONOMY')]: combinedFuelEfficiency,
			[labels.get('EV_BATTERY_WARRANTY')]: batteryWarrantyValue,
			[labels.get('TRANSMISSION')]: transmissionValue,
			[labels.get('DRIVETRAIN')]: driveLine,
			[labels.get('ENGINE')]: engineValue
		};
	}

	const evQuickSpecs = EV_QUICK_SPECS.map((evItem) => {
		if (Object.keys(quickSpecs).includes(evItem.key)) {
			return {
				...evItem,
				value: quickSpecs[evItem.key],
				isPicked: false
			};
		}
		return evItem;
	}).filter((item) => item.value);

	const evDetailsData = getEvDetailsContent(labels, evQuickSpecs, true);

	const arrayKeys = Object.keys(specObj);

	return arrayKeys
		.filter((key) => (key !== 'FUEL_ECONOMY' ? specObj[key] : true)) // remove keys that don't have a value
		.map((key) => {
			if (key === labels.get('COMBINED_FUEL_ECONOMY') && !isElectric) {
				return null;
			}

			if (key === labels.get('FUEL_ECONOMY')) {
				if (isElectric) {
					return null;
				}
				const fuelDetailContents = getEvDetailsContent(
					labels,
					'EV_MPG_SPEC_DETAILS_VERBIAGE'
				);
				const FuelDetailLabel = labels.get('EV_MPG_SPEC_TITLE');
				const renderDetailLink = fuelDetailContents &&
					fuelDetailContents?.details &&
					showEvDetailsLinkFlag && (
						<DetailsLink
							onClickDetails={() =>
								onOpenModal(FuelDetailLabel, fuelDetailContents)
							}
						/>
					);
				return [
					<dt key={`${key}_name`} className="text-muted col-xs-5">
						<span>{key}</span>
					</dt>,
					<dd key={`${key}_val`} className="col-xs-7 p-0">
						<span
							className="mr-3"
							dangerouslySetInnerHTML={{
								__html: specObj[key]
							}}
						/>
						{renderDetailLink}
					</dd>
				];
			}
			return [
				<dt key={`${key}_name`} className="text-muted col-xs-5">
					{key}
				</dt>,
				<dd key={`${key}_val`} className="col-xs-7 p-0">
					{key === labels.get('EXTERIOR_COLOR') && extColorSwatch}
					{key === labels.get('INTERIOR_COLOR') && intColorSwatch}
					<span
						className="mr-3"
						dangerouslySetInnerHTML={{ __html: specObj[key] }}
					/>
					{evQuickSpecs.map((item) => {
						if (
							item.value === specObj[key] &&
							showEvDetailsLinkFlag
						) {
							return (
								<DetailsLink
									key={`${item.key}_detailsLink`}
									onClickDetails={() =>
										openEvQuickSepcModal(
											labels,
											onOpenModal,
											item.key,
											evDetailsData
										)
									}
								/>
							);
						}
						return null;
					})}
				</dd>
			];
		});
};

export default function QuickSpecs({
	quickSpecs,
	vehicleType,
	deviceType,
	labels,
	isElectric,
	onOpenModal
}) {
	const flags = useFlags();

	if (
		Object.keys(quickSpecs).length === 0 &&
		quickSpecs.constructor === Object
	) {
		return null;
	}

	const useIDCAttrs = flags[FLAG_USE_QUICK_SPECS_FROM_IDC];
	const showEvDetailsLinkFlag = flags[FLAG_ENABLE_EV_DETAIL_LINK];
	const renderedSpecs = renderSpecs({
		quickSpecs,
		labels,
		vehicleType,
		isElectric,
		onOpenModal,
		showEvDetailsLinkFlag,
		useIDCAttrs
	}).filter((spec) => spec !== null);

	if (deviceType === 'MOBILE' || renderedSpecs.length === 1) {
		return (
			<dl className="line-height-condensed dl-horizontal row list-spaced">
				{renderedSpecs}
			</dl>
		);
	}

	const middleItemIndex = Math.ceil(renderedSpecs.length / 2);
	const firstColumn = renderedSpecs.slice(0, middleItemIndex);
	const secondColumn = renderedSpecs.slice(middleItemIndex);

	return (
		<div className="row">
			<div className="col-xs-6">
				<dl className="line-height-condensed dl-horizontal row list-spaced">
					{firstColumn}
				</dl>
			</div>
			<div className="col-xs-6">
				<dl className="line-height-condensed dl-horizontal row list-spaced">
					{secondColumn}
				</dl>
			</div>
		</div>
	);
}

QuickSpecs.propTypes = {
	quickSpecs: PropTypes.shape({}),
	deviceType: PropTypes.string.isRequired,
	labels: PropTypes.shape({
		get: PropTypes.func.isRequired
	}).isRequired,
	vehicleType: PropTypes.string,
	isElectric: PropTypes.bool,
	onOpenModal: PropTypes.func
};

QuickSpecs.defaultProps = {
	quickSpecs: {},
	vehicleType: '',
	isElectric: false
};
